import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseExpandIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link, useHistory, useLocation } from 'react-router-dom';
import ParentIcon from '@mui/icons-material/AccountBalanceOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useHotkeys } from 'react-hotkeys-hook';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { Radio, Tooltip } from '@mui/material';
import cx from 'classnames';

import {
  bootstrapComposerWebPreferences,
  displayRole,
  getPreviewAccessToken,
  setPreviewAccessToken,
  setRefreshTokenLocalStorage,
  setTokenLocalStorage,
  useDimensions,
} from '../../../helpers';
import AppLoader from '../../../components/modules/FullPageLoader/AppLoader';
import FileManagementIcon from '../../../assets/icons/folder-open.svg';
import { ContainedButton } from '../../../components/modules/Buttons';
import useOnClickOutside from '../../../helpers/useOnClickOutside';
import AmplifyLogo from '../../../assets/amplify-logo.svg';
import MyGroupIcon from '../../../assets/icons/folder-open.svg';
import RolePlate from '../../../components/modules/RolePlate';
import ProfileIcon from '../../../assets/icons/profile.svg';
import RequestHelpIcon from '../../../assets/icons/request.svg';
import SettingsIcon from '../../../assets/icons/settings.svg';
import UserListIcon from '../../../assets/icons/user.svg';
import LogoutIcon from '../../../assets/icons/logout.svg';
import Avatar from '../../../components/modules/Avatar';
import Search from '../../../components/modules/Search';
import { getMyUserInfo, getRecentDownloadJobs, patchSwitchOrganizartion, postOranizationHoursUsageRefresh } from '../../../api';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { saveUser } from '../../../store/user/userSlice';
import BellIcon from '../../../assets/icons/bell.svg';
import MainLogo from '../../../assets/main-logo.svg';
import RestorePasswordModal from './RestorePassword';
import { ORGANIZATION } from '../../../enums';
import { ROLES, RolesLowerCase } from '../../../enums/ROLES';
import { PATHS } from '../../../enums/PATHS';
import Notifications from './Notifications';
import RegisterModal from './SelfRegister';
import ProfileModal from './ProfileModal';
import PreferencesModal from './PreferencesModal';
import styles from './Header.module.scss';
import GlobalSearch from '../GlobalSearch';
import LogOutModal from './LogOutModal';
import LoginModal from './LoginModal';
import MyOrgModal from './MyOrgModal';
import { FE_VERSION } from '../../../consts/version';
import { getBackendVersion } from '../../../api/health';
import RequestHelpModal from './RequestHelpModal';
import PreviewModeButtons from './PreviewModeButtons';
import CancelRequestHelpModal from './CancelRequestHelpModal';
import { Portal } from '../../../helpers/bodyPortal';
import SpecialOperationsModal from './SpecialOperationsModal';
import PublicDownloadLinkExpiryModal from './PublicDownloadLinkExpiryModal';

const DOWNLOAD_JOBS_DEBUG_HOSTS = new Set(['dev.amplify.site', 'localhost']);
const isDownloadJobsDebugEnabledForCurrentHost = () => {
  if (typeof window === 'undefined') return false;
  return DOWNLOAD_JOBS_DEBUG_HOSTS.has(window.location.hostname);
};

const Header = () => {
  const [backendVersion, setBackendVersion] = useState('Loading…');
  const versionRequestPending = useRef(false);
  const loadBackendVersion = async () => {
    if (versionRequestPending.current) return;
    versionRequestPending.current = true;
    setBackendVersion('Loading…');
    try {
      setBackendVersion(await getBackendVersion() || 'Unavailable');
    } catch {
      setBackendVersion('Unavailable');
    } finally {
      versionRequestPending.current = false;
    }
  };
  const history = useHistory();
  const { widthD } = useDimensions();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const autoOpenedLoginRef = useRef(false);
  const confirmSARedirectRef = useRef<HTMLDivElement>(null);
  const confirmUserRedirectRef = useRef<HTMLDivElement>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showRestorePasswordModal, setShowRestorePasswordModal] = useState(false);
  const [showLogOutModal, setShowLogOutModal] = useState(false);
  const previewToken = getPreviewAccessToken();
  const location = useLocation();
  const switchLogOutModal = () => {
    setShowLogOutModal(!showLogOutModal);
  };
  const switchRestoreModal = () => {
    setShowRestorePasswordModal(!RestorePasswordModal);
  };
  const switchRegisterModal = () => {
    setShowRegisterModal(!showRegisterModal);
  };
  const switchModal = () => {
    setShowLoginModal(!showLoginModal);
  };

  const switchOrgModal = () => {
    setShowOrgModal(!showOrgModal);
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const registerValue = searchParams.get('register');
    if (registerValue === 'true') {
      setShowRegisterModal(true);
    }
    const forgotPass = searchParams.get('restore');
    if (forgotPass === 'true') {
      setShowRestorePasswordModal(true);
    }
  }, [location]);

  const switchProfileModal = () => {
    closeMenu();
    setShowProfileModal(!showProfileModal);
  };
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const openPreferencesModal = () => {
    closeMenu();
    setShowPreferencesModal(true);
  };
  const closePreferencesModal = () => {
    setShowPreferencesModal(false);
  };
  const [showSpecialOperationsModal, setShowSpecialOperationsModal] = useState(false);
  const [showPublicDownloadLinkExpiryModal, setShowPublicDownloadLinkExpiryModal] = useState(false);
  const closeSpecialOperationsModal = () => {
    setShowSpecialOperationsModal(false);
  };
  const openSpecialOperationsModal = () => {
    closeMenu();
    setShowSpecialOperationsModal(true);
  };
  const closePublicDownloadLinkExpiryModal = () => {
    setShowPublicDownloadLinkExpiryModal(false);
  };
  const openPublicDownloadLinkExpiryModal = () => {
    closeMenu();
    setShowPublicDownloadLinkExpiryModal(true);
  };

  const { currentUser, role, myOrganization, authChecked } = useAppSelector((state) => state.user);
  const { unReadCount, isSidebarHidden } = useAppSelector((state) => state.sharedStore);
  const sidebarVisible = !isSidebarHidden && ![PATHS.ALL_CONTENT.path, PATHS.ALL_CONTENT_VIDEOFEEDS.path].includes(location.pathname);
  const isDownloadJobsDebugEnabled = isDownloadJobsDebugEnabledForCurrentHost();
  const authSearchParams = new URLSearchParams(location.search);
  const hasAuthModalQuery = authSearchParams.get('register') === 'true' || authSearchParams.get('restore') === 'true';
  const loginRequired = location.pathname === PATHS.MAIN.path && authChecked && !currentUser && !previewToken && !hasAuthModalQuery;

  useEffect(() => {
    if (location.pathname !== PATHS.MAIN.path) {
      autoOpenedLoginRef.current = false;
      return;
    }

    if (!loginRequired || autoOpenedLoginRef.current) {
      return;
    }

    autoOpenedLoginRef.current = true;
    setShowLoginModal(true);
  }, [location.pathname, loginRequired]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const [navAnchorEl, setNavAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigationOpen = Boolean(navAnchorEl);
  const openNavigationMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNavAnchorEl(event.currentTarget);
  };
  const closeNavigationMenu = () => {
    setNavAnchorEl(null);
  };

  const [showNotifications, setShowNotifications] = useState(false);
  const switchNotifications = () => {
    setShowNotifications((prev) => !prev);
  };
  const openNotifications = () => {
    setShowNotifications(true);
  };
  const closeNotifications = () => {
    setShowNotifications(false);
  };

  useEffect(() => {
    if (!currentUser?.id) return;
    void bootstrapComposerWebPreferences();
  }, [currentUser?.id]);

  const onRedirect = () => {
    if (role === ROLES.SUPER_ADMIN) {
      history.push(PATHS.ADMIN_PAGE.path);
      return;
    }

    if (!myOrganization) return;

    if (role === ROLES.VIEWER) {
      history.push(PATHS.SHARED_WITH_ME.path);
      return;
    }

    if (role === ROLES.ORGANIZATION_ADMIN && myOrganization.type === 'parent') {
      history.push(PATHS.COLLABORATIVE_FOLDERS.path);
      return;
    }

    history.push(PATHS.MY_FOLDERS.path);
  };

  const [openListOfOrgs, setOpenListOfOrgs] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    let pending = false;
    const refreshOrganisations = async () => {
      if (document.visibilityState !== 'visible' || pending) return;
      pending = true;
      try {
        const user = await getMyUserInfo(true);
        if (!cancelled) dispatch(saveUser(user.data));
      } catch (err) {
        console.error('Could not refresh organisations', err);
      } finally {
        pending = false;
      }
    };
    if (openListOfOrgs) void refreshOrganisations();
    window.addEventListener('focus', refreshOrganisations);
    document.addEventListener('visibilitychange', refreshOrganisations);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshOrganisations);
      document.removeEventListener('visibilitychange', refreshOrganisations);
    };
  }, [currentUser?.id, previewToken, openListOfOrgs, dispatch]);

  const [selectedOrg, setSelectedOrg] = useState<ORGANIZATION | null>(null);
  const [selectedSA, setSelectedSA] = useState(false);
  const [searchOrg, setSearchOrg] = useState('');

  const onAdminSelect = () => {
    setSelectedOrg(null);
    setSelectedSA(true);
  };

  const onOrgSelect = (org: ORGANIZATION) => {
    setSelectedOrg(org);
    if (selectedSA) {
      setSelectedSA(false);
    }
  };

  useEffect(() => {
    if (myOrganization) {
      // @ts-ignore
      onOrgSelect(myOrganization);
    }
  }, [myOrganization]);

  const onOrgListModalClose = () => {
    setOpenListOfOrgs(false);
    // @ts-ignore
    setSelectedOrg(myOrganization ? myOrganization : null);
    setSearchOrg('');
    setSelectedSA(false);
  };

  const orgListModalRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(orgListModalRef, (e) => {
    e.stopPropagation();
    onOrgListModalClose();
  });

  const [orgChangeLoading, setOrgChangeLoading] = useState(false);
  const handleChangeOrg = async () => {
    try {
      setOrgChangeLoading(true);
      const res = await patchSwitchOrganizartion(selectedOrg?.id, selectedSA);

      if (previewToken) {
        setPreviewAccessToken(res.data.accessToken);
      } else {
        setTokenLocalStorage(res.data.accessToken);
        setRefreshTokenLocalStorage(res.data.refreshToken);
      }
      if (!selectedSA && history?.location?.pathname === PATHS.ADMIN_PAGE.path) {
        history.replace(PATHS.MY_FOLDERS.path);
        window.location.reload();
        return;
      }
      if (selectedSA) {
        history.replace(PATHS.ADMIN_PAGE.path);
        window.location.reload();
        return;
      }

      if (!selectedSA && history?.location?.pathname.includes(PATHS.SUB_GROUPS_MANAGEMENT.path)) {
        history.replace(PATHS.GROUPS_MANAGEMENT.path);
        window.location.reload();
        return;
      }

      if (selectedOrg?.type === 'parent') {
        history.replace(PATHS.COLLABORATIVE_FOLDERS.path);
        window.location.reload();
        return;
      }

      if (!selectedSA && history?.location?.pathname.includes(PATHS.TIMELINE.path)) {
        history.replace(PATHS.MY_FOLDERS.path);
        window.location.reload();
        return;
      } else if (!selectedSA && history?.location?.pathname.includes(PATHS.COMPOSER_PAGE.path)) {
        history.replace(PATHS.MY_REFLEX_COMPOSERS.path);
        window.location.reload();
        return;
      } else if (selectedOrg?.role?.title === RolesLowerCase.ANALYST || selectedOrg?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN) {
        history.replace(PATHS.MY_FOLDERS.path);
        window.location.reload();
        return;
      } else {
        history.replace(PATHS.MY_REFLEX_COMPOSERS.path);
        window.location.reload();
        return;
      }
    } catch (err) {
      setOrgChangeLoading(false);
      console.log(err);
    }
  };

  const [showRequestHelpModal, setShowRequestHelpModal] = useState(false);

  const switchRequestHelpModal = () => {
    closeMenu();
    setShowRequestHelpModal(!showRequestHelpModal);
  };

  const [showCancelRequestHelpModal, setShowCancelRequestHelpModal] = useState(false);

  const switchCancelRequestHelpModal = () => {
    closeMenu();
    setShowCancelRequestHelpModal(!showCancelRequestHelpModal);
  };

  const [minimizedPreviewMode, setMinimizePreviewMode] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const navigationSpaceRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const [navigationCollapsed, setNavigationCollapsed] = useState(false);

  useLayoutEffect(() => {
    const space = navigationSpaceRef.current;
    const navigation = navigationRef.current;
    if (!space || !navigation) return;
    let frame = 0;
    const measure = () => {
      const gap = parseFloat(getComputedStyle(space).columnGap) || 0;
      const search = space.querySelector<HTMLElement>(`.${styles.headerSearch}`);
      const required = navigation.getBoundingClientRect().width + (search ? search.getBoundingClientRect().width + gap : 0);
      setNavigationCollapsed((collapsed) => required + (collapsed ? 8 : 0) > space.clientWidth);
    };
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(space);
    observer.observe(navigation);
    measure();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  });

  useEffect(() => {
    const checkScrollbar = () => {
      if (headerRef.current) {
        setMinimizePreviewMode(headerRef.current.scrollWidth > headerRef.current.clientWidth);
      }
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
    };
  }, [previewToken]);

  const usageRefresh = async () => {
    if (!isDownloadJobsDebugEnabled) return;

    const formatDateTime = (value: any) => {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    };

    try {
      if (role === ROLES.SUPER_ADMIN) {
        try {
          await postOranizationHoursUsageRefresh();
        } catch (refreshErr) {
          console.log(refreshErr);
        }
      }

      const recentJobs = await getRecentDownloadJobs(10);
      const rows = Array.isArray(recentJobs?.jobs) ? recentJobs.jobs : [];
      const displayRows = rows.map((job: any) => ({
        ...job,
        usageCreatedAtLocal: formatDateTime(job?.usageCreatedAt),
        transcodeCreatedAtLocal: formatDateTime(job?.transcodeCreatedAt),
        transcodeUpdatedAtLocal: formatDateTime(job?.transcodeUpdatedAt),
        awsLiveQueriedAtLocal: formatDateTime(job?.awsLiveQueriedAt),
      }));
      console.groupCollapsed(`[download-jobs] latest ${rows.length} job(s)`);
      if (rows.length) {
        console.table(displayRows);
      } else {
        console.log('No recent download jobs found for current user.');
      }
      console.groupEnd();
    } catch (err) {
      console.error('[download-jobs] failed to fetch recent jobs', err);
    }
  };

  const onMobileNavigate = (path: string) => {
    closeNavigationMenu();
    history.push(path);
  };

  const openMobileOrgSwitcher = () => {
    closeNavigationMenu();
    setOpenListOfOrgs(true);
  };

  const mobileNavigationItems: { label: string; path: string }[] = [];
  if (role === ROLES.ANALYST) {
    mobileNavigationItems.push({ label: 'Groups', path: PATHS.GROUPS_MANAGEMENT.path });
    mobileNavigationItems.push({ label: 'Files', path: PATHS.MY_FOLDERS.path });
  }
  if (role === ROLES.SUPER_ADMIN) {
    mobileNavigationItems.push({ label: 'Users', path: PATHS.SA_USERS_LIST.path });
    mobileNavigationItems.push({ label: 'Organisations', path: PATHS.ADMIN_PAGE.path });
  }
  if (role === ROLES.ORGANIZATION_ADMIN) {
    if (myOrganization?.type !== 'parent') {
      mobileNavigationItems.push({ label: 'Users', path: PATHS.USERS_LIST.path });
    }
    if (myOrganization?.type === 'parent') {
      mobileNavigationItems.push({ label: 'Orgs', path: PATHS.CHILD_ORG_LIST.path });
    }
    mobileNavigationItems.push({
      label: 'Groups',
      path: myOrganization?.type !== 'parent' ? PATHS.GROUPS_MANAGEMENT.path : PATHS.ORG_GROUPS_MANAGEMENT.path,
    });
    mobileNavigationItems.push({
      label: myOrganization?.type === 'parent' ? 'Folders' : 'Files',
      path: myOrganization?.type === 'parent' ? PATHS.COLLABORATIVE_FOLDERS.path : PATHS.MY_FOLDERS.path,
    });
    mobileNavigationItems.push({ label: 'Admin', path: PATHS.ALL_CONTENT.path });
  }
  if (role === ROLES.VIEWER) {
    mobileNavigationItems.push({ label: 'Groups', path: PATHS.GROUPS_MANAGEMENT.path });
    mobileNavigationItems.push({ label: 'Files', path: PATHS.SHARED_WITH_ME.path });
  }
  const canUseGlobalSearch = Boolean(role && role !== ROLES.SUPER_ADMIN);
  const canSwitchOrganisation = Boolean(
    currentUser?.organizations?.length &&
      (role === ROLES.SUPER_ADMIN || currentUser.isSuperAdmin || currentUser.organizations.length > 1),
  );

  useHotkeys('Enter', () => {
    if (!openListOfOrgs) return;

    if (currentUser && currentUser?.organizations) {
      if (currentUser?.organizations.length > 0 && role === ROLES.SUPER_ADMIN && currentUser?.server_name) {
        confirmSARedirectRef?.current?.click();
        return;
      }

      if (role !== ROLES.SUPER_ADMIN && myOrganization && (currentUser?.isSuperAdmin || currentUser?.organizations?.length > 1)) {
        confirmUserRedirectRef?.current?.click();
        return;
      }
    }
  });

  return (
    <>
      <header ref={headerRef} className={styles.header}>
        <div className={styles.leftSide}>
          <Tooltip onOpen={loadBackendVersion} title={
            <>
              <div>Web client: {FE_VERSION}</div>
              <div>Server: {backendVersion}</div>
            </>
          }>
            <div className={styles.logoGroup}>
              {currentUser ? (
                <div className={styles.loggedInLogo} onClick={onRedirect}>
                  <img src={AmplifyLogo} alt="Amplify logo" className={styles.amplifyLogo} />
                  <span>AMPLIFY</span>
                </div>
              ) : (
                <>
                  <img src={MainLogo} alt="logo" className={styles.mainLogo} />
                  <img src={AmplifyLogo} alt="Amplify logo" className={styles.amplifyLogo} />
                </>
              )}
            </div>
          </Tooltip>

          <div className={styles.myOrg}>
            {role === ROLES.SUPER_ADMIN && currentUser?.server_name && (
              <>
                <div>
                  <div className={styles.serverAvatar}>
                    <span className={styles.serverName}>{currentUser.server_name[0]}</span>
                  </div>
                </div>
                <div className={styles.currentOrgInfo}>
                  <div>
                    <div className={styles.currentOrgTitle}>{currentUser.server_name}</div>
                    <div className={styles.currentOrgLocation}>Server</div>
                  </div>

                  {currentUser && currentUser?.organizations && currentUser?.organizations.length > 0 && (
                    <>
                      <div className={styles.switcher}>
                        {openListOfOrgs ? (
                          <CloseExpandIcon onClick={onOrgListModalClose} />
                        ) : (
                          <ExpandMoreIcon onClick={() => setOpenListOfOrgs(true)} />
                        )}
                      </div>
                      <div className={styles.orgModal}>
                        {openListOfOrgs && (
                          <Portal>
                            <div className={styles.orgsModal} ref={orgListModalRef}>
                              <div className={styles.orgList}>
                                <Search
                                  placeHolder="Search by name"
                                  className={styles.search}
                                  onChange={(e) => setSearchOrg(e.target.value)}
                                />
                                {currentUser?.organizations
                                  ?.filter(
                                    (org) =>
                                      org.name.toLocaleLowerCase().includes(searchOrg.toLocaleLowerCase()) ||
                                      org.location.toLocaleLowerCase().includes(searchOrg.toLocaleLowerCase()),
                                  )
                                  .map((org) => (
                                    <div
                                      key={org.id}
                                      onClick={() => onOrgSelect(org)}
                                      className={cx(styles.org, selectedOrg?.id === org.id ? styles.orgSelected : '')}
                                    >
                                      <Avatar avatar={org?.image_url} name={org.name} />
                                      {org.type === 'parent' && <ParentIcon style={{ fill: '#6500aa', marginLeft: '8px' }} />}
                                      <div className={styles.orgInfo}>
                                        <div className={styles.orgTitle}>{org.name}</div>
                                        <div className={styles.orgLocation}>{org.location}</div>{' '}
                                      </div>
                                      <div className={styles.rightSideOfOrg}>
                                        {org?.role && <RolePlate className={styles.rolePlate} userRole={displayRole(org?.role?.title)} />}
                                        {selectedOrg?.id === org.id && (
                                          <Radio
                                            style={{ color: selectedOrg.id === org.id ? '#39b3ea' : '#595D62' }}
                                            checked={selectedOrg.id === org.id}
                                            className={styles.radioButton}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                              <div className={styles.changeOrgAction}>
                                <ContainedButton
                                  className={styles.changeOrgBtn}
                                  onClick={handleChangeOrg}
                                  disabled={selectedOrg?.id === myOrganization.id || (!selectedOrg && !selectedSA)}
                                >
                                  <div ref={confirmSARedirectRef}>{selectedSA ? 'To Admin Page' : 'Change Organisation'}</div>
                                </ContainedButton>
                              </div>
                            </div>
                          </Portal>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            {role !== ROLES.SUPER_ADMIN && myOrganization && (
              <>
                {' '}
                <div style={{ cursor: 'pointer' }} onClick={switchOrgModal}>
                  {myOrganization?.name && (
                    <Avatar className={styles.orgAvatar} avatar={myOrganization?.image_url} name={myOrganization?.name} />
                  )}
                </div>
                <div className={styles.currentOrgInfo}>
                  <div>
                    <div className={styles.currentOrgTitle} onClick={switchOrgModal}>
                      {myOrganization?.name}
                    </div>{' '}
                    <div className={styles.currentOrgLocation} onClick={switchOrgModal}>
                      {myOrganization?.location}
                    </div>{' '}
                  </div>
                  {currentUser && currentUser?.organizations && (currentUser?.isSuperAdmin || currentUser?.organizations?.length > 1) && (
                    <>
                      <div className={styles.switcher}>
                        {openListOfOrgs ? (
                          <CloseExpandIcon onClick={onOrgListModalClose} />
                        ) : (
                          <ExpandMoreIcon onClick={() => setOpenListOfOrgs(true)} />
                        )}
                      </div>
                      <div className={styles.orgModal}>
                        {openListOfOrgs && (
                          <Portal>
                            <div className={styles.orgsModal} ref={orgListModalRef}>
                              <div className={styles.orgList}>
                                <Search
                                  placeHolder="Search by name"
                                  className={styles.search}
                                  onChange={(e) => setSearchOrg(e.target.value)}
                                />
                                {currentUser?.organizations
                                  ?.filter(
                                    (org) =>
                                      org.name.toLocaleLowerCase().includes(searchOrg.toLocaleLowerCase()) ||
                                      org.location.toLocaleLowerCase().includes(searchOrg.toLocaleLowerCase()),
                                  )
                                  .map((org) => (
                                    <div
                                      key={org.id}
                                      onClick={() => onOrgSelect(org)}
                                      className={cx(styles.org, selectedOrg?.id === org.id ? styles.orgSelected : '')}
                                    >
                                      <Avatar avatar={org?.image_url} name={org.name} />
                                      {org.type === 'parent' && <ParentIcon style={{ fill: '#6500aa', marginLeft: '8px' }} />}
                                      <div className={styles.orgInfo}>
                                        <div className={styles.orgTitle}>{org.name}</div>
                                        <div className={styles.orgLocation}>{org.location}</div>{' '}
                                      </div>
                                      <div className={styles.rightSideOfOrg}>
                                        {org?.role && <RolePlate className={styles.rolePlate} userRole={displayRole(org?.role?.title)} />}
                                        {selectedOrg?.id === org.id && (
                                          <Radio
                                            style={{ color: selectedOrg.id === org.id ? '#39b3ea' : '#595D62' }}
                                            checked={selectedOrg.id === org.id}
                                            className={styles.radioButton}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                {currentUser?.isSuperAdmin && !previewToken && (
                                  <div
                                    style={{ padding: '8px', height: '38px' }}
                                    onClick={onAdminSelect}
                                    className={cx(styles.org, selectedSA ? styles.orgSelected : '')}
                                  >
                                    <RolePlate userRole={'Super Admin'} style={{ fontSize: '15px' }} />
                                    <div className={styles.orgInfo}>
                                      <div className={styles.orgTitle} style={{ color: 'white' }}>
                                        Login as Super Admin
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className={styles.changeOrgAction}>
                                <ContainedButton
                                  className={styles.changeOrgBtn}
                                  onClick={handleChangeOrg}
                                  disabled={selectedOrg?.id === myOrganization.id || (!selectedOrg && !selectedSA)}
                                >
                                  <div ref={confirmUserRedirectRef}>{selectedSA ? 'To Admin Page' : 'Change Organisation'}</div>
                                </ContainedButton>
                              </div>
                            </div>
                          </Portal>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div ref={navigationSpaceRef} className={styles.leftBtns}>
            {canUseGlobalSearch && !sidebarVisible && location.pathname !== PATHS.ALL_CONTENT.path && (
              <GlobalSearch compact placement="bottom" className={styles.headerSearch}
                allContent={location.pathname === PATHS.ALL_CONTENT_VIDEOFEEDS.path} />
            )}
            {navigationCollapsed && (
              <>
                {role && mobileNavigationItems.length > 0 && (
                  <>
                    <ContainedButton
                      ariaLabel="Navigation"
                      className={styles.mobileNavigationBtn}
                      kind="default"
                      onClick={openNavigationMenu}
                    >
                      <MenuIcon className={styles.mobileMenuIcon} />
                    </ContainedButton>
                    <Menu
                      id="mobile-navigation-menu"
                      anchorEl={navAnchorEl}
                      open={navigationOpen}
                      onClose={closeNavigationMenu}
                      sx={{
                        '.MuiMenu-paper': {
                          background: '#0A1929',
                        },
                        '& .MuiList-root': {
                          background: '#0A1929',
                          color: 'white',
                          padding: '6px',
                          minWidth: '150px',
                        },
                      }}
                    >
                      {canSwitchOrganisation && (
                        <MenuItem onClick={openMobileOrgSwitcher} className={styles.mobileNavItem} divider>
                          Switch organisation
                        </MenuItem>
                      )}
                      {mobileNavigationItems.map((item) => (
                        <MenuItem
                          key={`${item.label}_${item.path}`}
                          onClick={() => onMobileNavigate(item.path)}
                          className={styles.mobileNavItem}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </>
            )}
            <div ref={navigationRef} className={cx(styles.navigation, navigationCollapsed && styles.navigationHidden)} aria-hidden={navigationCollapsed || undefined}>
                {role && (
                  <>
                    {role === ROLES.ANALYST && (
                      <>
                        <Link className={styles.headerLink} to={PATHS.GROUPS_MANAGEMENT.path}>
                          <div>
                            {widthD > 840 && <img src={MyGroupIcon} alt="my group" />}
                            <span>Groups</span>
                          </div>
                        </Link>
                        <Link className={styles.headerLink} to={PATHS.MY_FOLDERS.path}>
                          <div>
                            {widthD > 840 && <img src={FileManagementIcon} alt="file management" />}
                            <span>Files</span>
                          </div>
                        </Link>
                      </>
                    )}
                    {role === ROLES.SUPER_ADMIN && (
                      <>
                        <Link className={styles.headerLink} to={PATHS.SA_USERS_LIST.path}>
                          <div>
                            {widthD > 840 && <img src={UserListIcon} style={{ height: '14px' }} />}
                            <span>Users</span>
                          </div>
                        </Link>
                        <Link className={styles.headerLink} to={PATHS.ADMIN_PAGE.path}>
                          <div>
                            {widthD > 840 && <img src={MyGroupIcon} alt="my group" />}
                            <span>Orgs</span>
                          </div>
                        </Link>
                      </>
                    )}
                    {role === ROLES.ORGANIZATION_ADMIN && (
                      <>
                        {myOrganization?.type !== 'parent' && (
                          <Link className={styles.headerLink} to={PATHS.USERS_LIST.path}>
                            <div>
                              {widthD > 840 && <img src={UserListIcon} style={{ height: '14px' }} />}
                              <span>Users</span>
                            </div>
                          </Link>
                        )}

                        {myOrganization?.type === 'parent' && (
                          <Link className={styles.headerLink} to={PATHS.CHILD_ORG_LIST.path}>
                            <div>
                              {widthD > 840 && <img src={MyGroupIcon} style={{ height: '14px' }} />}
                              <span>Orgs</span>
                            </div>
                          </Link>
                        )}

                        <Link
                          className={styles.headerLink}
                          to={myOrganization?.type !== 'parent' ? PATHS.GROUPS_MANAGEMENT.path : PATHS.ORG_GROUPS_MANAGEMENT.path}
                        >
                          <div>
                            {widthD > 840 && <img src={MyGroupIcon} alt="groups management" />}
                            <span>Groups</span>
                          </div>
                        </Link>
                        {myOrganization?.type === 'parent' ? (
                          <Link className={styles.headerLink} to={PATHS.COLLABORATIVE_FOLDERS.path}>
                            <div>
                              {widthD > 840 && <img src={FileManagementIcon} alt="file management" />}
                              <span> Folders </span>
                            </div>
                          </Link>
                        ) : (
                          <Link className={styles.headerLink} to={PATHS.MY_FOLDERS.path}>
                            <div>
                              {widthD > 840 && <img src={FileManagementIcon} alt="file management" />}
                              <span>Files</span>
                            </div>
                          </Link>
                        )}
                        <Link className={styles.headerLink} to={PATHS.ALL_CONTENT.path}>
                          <div>
                            {widthD > 840 && <img src={FileManagementIcon} alt="content admin" />}
                            <span>Admin</span>
                          </div>
                        </Link>
                      </>
                    )}
                    {role === ROLES.VIEWER && (
                      <>
                        <Link className={styles.headerLink} to={PATHS.GROUPS_MANAGEMENT.path}>
                          <div>
                            {widthD > 840 && <img src={MyGroupIcon} alt="groups" />}
                            <span>Groups</span>
                          </div>
                        </Link>
                        <Link className={styles.headerLink} to={PATHS.SHARED_WITH_ME.path}>
                          <div>
                            {widthD > 840 && <img src={FileManagementIcon} alt="file management" />}
                            <span>Files</span>
                          </div>
                        </Link>
                      </>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>

        {role !== ROLES.SUPER_ADMIN && previewToken && <PreviewModeButtons minimizedPreviewMode={minimizedPreviewMode} />}
        <div className={styles.rightSide}>
          {currentUser ? (
            <>
              {role !== ROLES.SUPER_ADMIN && (
                <div className={styles.notificationBell}>
                  <div key={'right'}>
                    <div onClick={switchNotifications} className={styles.notificationIcon}>
                      {unReadCount > 0 ? <div className={styles.readCount}>+{unReadCount}</div> : <></>}
                      <img src={BellIcon} alt="notifications" className={styles.bellIcon} />
                    </div>
                    <SwipeableDrawer
                      anchor={'right'}
                      open={showNotifications}
                      onClose={closeNotifications}
                      onOpen={openNotifications}
                      PaperProps={{
                        sx: {
                          height: 'fit-content',
                          borderRadius: 'var(--ui-border-radius)',
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <Notifications toggleOff={closeNotifications} open={showNotifications} />
                    </SwipeableDrawer>
                  </div>
                </div>
              )}

              <Avatar className={styles.userAvatar} avatar={currentUser?.avatar_url} name={currentUser?.username} />
              <div className={styles.user}>
                <div className={styles.name}>{currentUser?.username ? currentUser?.username : role}</div>
                <div className={styles.email}>{currentUser?.email}</div>
              </div>

              {
                <>
                  <div className={styles.chevronDown} onClick={openMenu}>
                    <KeyboardArrowDownIcon />
                  </div>
                  <Menu
                    id="basic-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={closeMenu}
                    MenuListProps={{
                      'aria-labelledby': 'basic-button',
                    }}
                    sx={{
                      '.MuiMenu-paper': {
                        background: open ? '#0A1929' : '',
                      },
                      '& .MuiList-root': {
                        background: '#0A1929',
                        color: 'white',
                        width: '225px',
                        display: 'flex',
                        flexDirection: 'column',
                        textAlign: 'left',
                        alignItems: 'flex-start',
                        padding: '8px',
                        paddingBottom: '0px',
                      },
                    }}
                  >
                    <MenuItem key="placeholder" style={{ display: 'none', marginBottom: '8px', padding: '6px' }} />
                    <MenuItem onClick={switchProfileModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                      <img src={ProfileIcon} alt="my profile" />
                      <div className={styles.menuIcon}>Profile</div>
                    </MenuItem>
                    {role !== ROLES.SUPER_ADMIN && (
                      <MenuItem onClick={openPreferencesModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                        <img src={SettingsIcon} alt="preferences" />
                        <div className={styles.menuIcon}>Preferences</div>
                      </MenuItem>
                    )}
                    {[ROLES.SUPER_ADMIN, ROLES.ORGANIZATION_ADMIN].includes(role as ROLES) && (
                      <MenuItem onClick={openPublicDownloadLinkExpiryModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                        <img src={SettingsIcon} alt="public download link expiry" />
                        <div className={styles.menuIcon}>Public Link Expiry</div>
                      </MenuItem>
                    )}
                    {role === ROLES.SUPER_ADMIN && (
                      <MenuItem onClick={openSpecialOperationsModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                        <img src={FileManagementIcon} alt="actions" style={{ height: '19px' }} />
                        <div className={styles.menuIcon}>Actions</div>
                      </MenuItem>
                    )}
                    {/* <MenuItem onClick={switchOrgModal} style={{ marginBottom: '8px', padding: '6px' }}>
                      <img src={MyOrgIcon} alt="my organization" />
                      <div className={styles.menuIcon}>My organization</div>
                    </MenuItem> */}

                    {!previewToken &&
                      role !== ROLES.SUPER_ADMIN &&
                      (!currentUser?.need_help ? (
                        <MenuItem onClick={switchRequestHelpModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                          <img src={RequestHelpIcon} alt="request help" style={{ marginRight: '3px' }} />
                          <div className={styles.menuIcon}>Request Help</div>
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={switchCancelRequestHelpModal} style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                          <img src={RequestHelpIcon} alt="request help" style={{ marginRight: '3px' }} />
                          <div className={styles.menuIcon}>Cancel the request</div>
                        </MenuItem>
                      ))}
                    <MenuItem style={{ marginBottom: '8px', padding: '6px', width: '100%' }}>
                      {isDownloadJobsDebugEnabled ? (
                        <div onClick={usageRefresh} className={`${styles.roleDropdown}`}>
                          <RolePlate className={styles.rolePlate} userRole={displayRole(currentUser.role.title)} />
                          <span className={styles.roleTitle}>{currentUser.role.title.replace('Organization', 'Organisation')}</span>
                        </div>
                      ) : (
                        <div className={`${styles.roleDropdown}`}>
                          <RolePlate className={styles.rolePlate} userRole={displayRole(currentUser.role.title)} />
                          <span className={styles.roleTitle}>{currentUser.role.title.replace('Organization', 'Organisation')}</span>
                        </div>
                      )}
                    </MenuItem>
                  </Menu>
                </>
              }

              {!previewToken && (
                <div className={styles.logout}>
                  <ContainedButton className={styles.logoutBtn} onClick={switchLogOutModal}>
                    <div className={styles.logoutAction}>
                      <img src={LogoutIcon} alt="logout" />
                      <span>Logout</span>
                    </div>
                  </ContainedButton>
                </div>
              )}
            </>
          ) : (
            <div>
              <ContainedButton onClick={switchModal} className={styles.login} kind="dark">
                Log In
              </ContainedButton>
            </div>
          )}
        </div>
        <LoginModal switchModal={switchModal} open={showLoginModal} disableClose={loginRequired} />
        <MyOrgModal switchModal={switchOrgModal} open={showOrgModal} />
        <RegisterModal switchModal={switchRegisterModal} open={showRegisterModal} />
        <RestorePasswordModal switchModal={switchRestoreModal} open={showRestorePasswordModal} />
        <ProfileModal switchModal={switchProfileModal} open={showProfileModal} />
        {role !== ROLES.SUPER_ADMIN && <PreferencesModal open={showPreferencesModal} onClose={closePreferencesModal} />}
        <LogOutModal switchModal={switchLogOutModal} open={showLogOutModal} />
        <RequestHelpModal switchModal={switchRequestHelpModal} open={showRequestHelpModal} />
        <CancelRequestHelpModal switchModal={switchCancelRequestHelpModal} open={showCancelRequestHelpModal} />
        <SpecialOperationsModal open={showSpecialOperationsModal} onClose={closeSpecialOperationsModal} />
        <PublicDownloadLinkExpiryModal open={showPublicDownloadLinkExpiryModal} onClose={closePublicDownloadLinkExpiryModal} />
      </header>
      {orgChangeLoading && <AppLoader />}
    </>
  );
};

export default Header;
